import clsx from 'clsx';
import { useMemo } from 'react';
import { GlobalStyles, List, styled } from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import NavGroup from './NavGroup';
import NavLink from './NavItem';
import { useMeCore } from '../../core/modules/me';
import GridViewIcon from '@mui/icons-material/GridView';
import InventoryIcon from '@mui/icons-material/Inventory';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MapIcon from '@mui/icons-material/Map';
import ArticleIcon from '@mui/icons-material/Article';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CategoryIcon from '@mui/icons-material/Category';
import TypeSpecimenIcon from '@mui/icons-material/TypeSpecimen';
import TransgenderIcon from '@mui/icons-material/Transgender';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ConstructionIcon from '@mui/icons-material/Construction';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RestoreIcon from '@mui/icons-material/Restore';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HelpIcon from '@mui/icons-material/Help';
import SpeedIcon from '@mui/icons-material/Speed';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AbcIcon from '@mui/icons-material/Abc';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { MoneyOutlined } from '@mui/icons-material';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';

interface Props {
  className?: string;
}

const inputGlobalStyles = (
  <GlobalStyles
    styles={() => ({
      '.popper-navigation-list': {
        '& .fuse-list-item': {
          padding: '8px 12px 8px 12px',
          height: 40,
          minHeight: 40,
          '& .fuse-list-item-text': {
            padding: '0 0 0 8px',
          },
        },
        '&.dense': {
          '& .fuse-list-item': {
            minHeight: 32,
            height: 32,
            '& .fuse-list-item-text': {
              padding: '0 0 0 8px',
            },
          },
        },
      },
    })}
  />
);

const StyledList = styled(List)(({ theme }) => ({
  '& .fuse-list-item': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0,0,0,.04)',
    },
    '&:focus:not(.active)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0,0,0,.05)',
    },
    padding: '8px 12px 8px 12px',
    height: 40,
    minHeight: 40,
    '&.level-0': {
      minHeight: 44,
      minminHeight: 44,
    },
    '& .fuse-list-item-text': {
      padding: '0 0 0 8px',
    },
  },
  '&.active-square-list': {
    '& .fuse-list-item': {
      borderRadius: '0',
    },
  },
}));

const Navigation = (props: Props) => {
  const { className } = props;
  const { user } = useMeCore();

  const showAdminPanel = useMemo(
    () => user && user.privileges.superAdmin,
    [user]
  );
  const showProducts = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readProduct ||
        user.privileges.writeProduct),
    [user]
  );
  const showBusiness = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readBusiness ||
        user.privileges.writeBusiness),
    [user]
  );
  const showContainers = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readContainer ||
        user.privileges.writeContainer),
    [user]
  );
  const showLocations = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readLocation ||
        user.privileges.writeLocation),
    [user]
  );
  const showWarehouses = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readWarehouse ||
        user.privileges.writeWarehouse),
    [user]
  );
  const showReceptions = useMemo(
    () =>
      user &&
      (user.privileges.superAdmin ||
        user.privileges.readReception ||
        user.privileges.writeReception),
    [user]
  );
  const showMasters = useMemo(
    () => user && (user.privileges.superAdmin || user.privileges.writeMaster),
    [user]
  );

  return (
    <>
      {inputGlobalStyles}
      <StyledList
        className={clsx(
          'navigation whitespace-nowrap items-center flex flex-col px-12',
          className
        )}
      >
        <NavLink
          title="Inicio"
          url="/dashboard/welcome"
          icon={<GridViewIcon />}
        />
        <NavGroup icon={<DashboardCustomizeIcon />} title="Administración">
          {showMasters && (
            <>
              <NavLink
                title="Estados"
                url="/dashboard/masters/attributes"
                icon={<TypeSpecimenIcon />}
              />
              <NavLink
                title="Tipos"
                url="/dashboard/masters/types"
                icon={<AbcIcon />}
              />
              <NavLink
                title="Categorías"
                url="/dashboard/masters/categories"
                icon={<CategoryIcon />}
              />
              <NavLink
                title="Formatos"
                url="/dashboard/masters/formats"
                icon={<FormatSizeIcon />}
              />
              <NavLink
                title="Géneros"
                url="/dashboard/masters/genres"
                icon={<TransgenderIcon />}
              />
              <NavLink
                title="Rango de Precios"
                url="/dashboard/masters/priceRanges"
                icon={<PriceChangeIcon />}
              />
            </>
          )}
          {showWarehouses && (
            <NavLink
              title="Bodegas"
              url="/dashboard/warehouses"
              icon={<StorefrontIcon />}
            />
          )}
          
          {showBusiness && (
            <NavLink
              title="Empresas"
              url="/dashboard/businesses"
              icon={<ApartmentIcon />}
            />
          )}
          
          {showLocations && (
            <NavLink
              title="Ubicaciones"
              url="/dashboard/locations"
              icon={<MapIcon />}
            />
          )}
        </NavGroup>
        <NavGroup icon={<ConstructionIcon />} title="Operaciones">
          {showProducts && (
            <NavLink
              title="Artículos"
              url="/dashboard/articles"
              icon={<ArticleIcon />}
            />
          )}
          {showContainers && (
            <NavLink
              title="Contenedores"
              url="/dashboard/containers"
              icon={<InventoryIcon />}
            />
          )}
          {showReceptions && (
            <>
              <NavLink
                title="Orden de Ingreso"
                url="/dashboard/operations/receptionOrders"
                icon={<ReceiptIcon />}
              />
              <NavLink
                title="Recepción"
                url="/dashboard/operations/receptions"
                icon={<LocalShippingIcon />}
              />
            </>
          )}
          <NavLink
            title="Contenedor Vigente"
            url="/dashboard/operations/valid-container"
            icon={<Inventory2Icon />}
          />
          <NavLink
            title="Clasificación Contenedor"
            url="/dashboard/operations/container-classification"
            icon={<QrCodeIcon />}
          />
          <NavLink
            title="Clasificación a Ubicación"
            url="/dashboard/operations/valorative-classification"
            icon={<DevicesOtherIcon />}
          />
          <NavLink
            title="Ubicar Contenedor"
            url="/dashboard/operations/locate-container"
            icon={<RestoreIcon />}
          />
          <NavLink
            title="Reclasificar"
            url="/dashboard/operations/reclassify"
            icon={<TravelExploreIcon />}
          />
          <NavLink
            title="Valorización"
            url="/dashboard/operations/valorization"
            icon={<AttachMoneyIcon />}
          />
          <NavLink
            title="Ajustes"
            url="/dashboard/operations/tools"
            icon={<BuildIcon />}
          />
          {/* <NavLink
            title="Pre clasificación"
            url="/dashboard/operations/pre-classification"
            icon={<ChecklistRtlIcon />}
          /> */}
        </NavGroup>
        <NavGroup icon={<HelpIcon />} title="Consultas" subtitle="Consultas">
          <NavLink
            title="Artículos"
            url="/dashboard/queries/articles"
            icon={<ArticleIcon />}
          />
          <NavLink
            title="Contenedores"
            url="/dashboard/queries/containers"
            icon={<Inventory2Icon />}
          />
          <NavLink
            title="Reporte de ubicaciones"
            url="/dashboard/queries/locations-report"
            icon={<MapIcon />}
          />
          <NavLink
            title="Auditoría de Contenedores"
            url="/dashboard/queries/container-audit"
            icon={<AssignmentIcon />}
          />
          {/* <NavLink
            title="Ubicaciones"
            url="/dashboard/queries/locations"
            icon={<LocationCityIcon />}
          />
          <NavLink
            title="Productividad"
            url="/dashboard/queries/productivity"
            icon={<SpeedIcon />}
          />
          <NavLink
            title="Contenido de contenedor"
            url="/dashboard/queries/container-content"
            icon={<Inventory2Icon />}
          />
          <NavLink
            title="Contenido Ubicación"
            url="/dashboard/queries/location-content"
            icon={<NotListedLocationIcon />}
          />
          <NavLink
            title="Revisión de artículos"
            url="/dashboard/queries/articles-check"
            icon={<Inventory2Icon />}
          /> */}
        </NavGroup>
        {/* <NavGroup
          icon={<MiscellaneousServicesIcon />}
          title="Otros"
          subtitle="Otros"
        >
          <NavLink
            title="E-commerce"
            url="/dashboard/ecommerce"
            icon={<ShoppingCartIcon />}
          />
        </NavGroup> */}
        {showAdminPanel && (
          <NavGroup
            icon={<SupervisorAccountIcon />}
            title="Panel de Admin"
            subtitle="Panel de Admin"
          >
            <NavLink
              title="Compañias"
              url="/dashboard/companies"
              icon={<LocationCityIcon />}
            />
            <NavLink
              title="Usuarios"
              url="/dashboard/users"
              icon={<GroupOutlinedIcon />}
            />
          </NavGroup>
        )}
      </StyledList>
    </>
  );
};

export default Navigation;
